// src/lib/password-service.ts

// --- TYPES ---
export interface PasswordSettings {
  passwordType: "smart" | "random" | "memorable" | "pin" | "passphrase";
  length: number;
  includeUppercase: boolean;
  includeLowercase: boolean;
  includeNumbers: boolean;
  includeSymbols: boolean;
  excludeSimilar: boolean;
  excludeAmbiguous: boolean;
  minDigits: number;
  minSymbols: number;
  wordCount: number;
  separator: string;
  capitalizeWords: boolean;
  addNumbers: boolean;
  pinLength: number;
}

// --- CONSTANTS ---
const CHARSETS = {
  UPPERCASE: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  LOWERCASE: "abcdefghijklmnopqrstuvwxyz",
  NUMBERS: "0123456789",
  SYMBOLS: "!@#$%^&*()_+-=[]{}|;:,.<>?",
  SIMILAR: /[il1Lo0O]/g,
  AMBIGUOUS: /[{}[\]()/\\'"`~,;:.<>]/g,
} as const;

const YEAR_SECONDS = 31_536_000;

// --- SECURE RANDOM ---
const secureRandom = (max: number): number => {
  const buf = new Uint32Array(1);
  window.crypto.getRandomValues(buf);
  return buf[0] % max;
};

const randChar = (pool: string): string => pool[secureRandom(pool.length)];
const takeN = (n: number, pool: string): string[] =>
  Array.from({ length: n }, () => randChar(pool));

// --- SHUFFLE ---
const shuffle = (arr: string[]): string[] => {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = secureRandom(i + 1);
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
};

// --- GENERATORS ---
export const generateRandomPassword = (o: PasswordSettings): string => {
  let available = [
    o.includeUppercase && CHARSETS.UPPERCASE,
    o.includeLowercase && CHARSETS.LOWERCASE,
    o.includeNumbers && CHARSETS.NUMBERS,
    o.includeSymbols && CHARSETS.SYMBOLS,
  ]
    .filter(Boolean)
    .join("") as string;

  if (o.excludeSimilar) available = available.replace(CHARSETS.SIMILAR, "");
  if (o.excludeAmbiguous) available = available.replace(CHARSETS.AMBIGUOUS, "");
  if (!available) return "Select character types";

  const req: string[] = [];
  if (o.includeNumbers) req.push(...takeN(o.minDigits, CHARSETS.NUMBERS));
  if (o.includeSymbols) req.push(...takeN(o.minSymbols, CHARSETS.SYMBOLS));

  const remaining = o.length - req.length;
  req.push(...takeN(remaining, available));

  return shuffle(req).join("");
};

export const generatePassphrase = (o: PasswordSettings, wordlist: string[]): string => {
  if (!wordlist || wordlist.length === 0) return "Wordlist loading...";
  const cap = (w: string) => (o.capitalizeWords ? w[0].toUpperCase() + w.slice(1) : w);

  const words = Array.from({ length: o.wordCount }, () => cap(wordlist[secureRandom(wordlist.length)]));
  let passphrase = words.join(o.separator);
  if (o.addNumbers) passphrase += String(secureRandom(1000)); // 0..999
  return passphrase;
};

// --- ANALYSIS ---
export const calculateEntropy = (s: PasswordSettings, wordlistSize: number): number => {
  switch (s.passwordType) {
    case "passphrase":
    case "memorable": {
      if (!wordlistSize) return 0;
      const perWord = Math.log2(wordlistSize);
      let total = s.wordCount * perWord;
      if (s.capitalizeWords) total += s.wordCount; // rough bump
      if (s.addNumbers) total += Math.log2(1000);
      return +total.toFixed(2);
    }
    case "pin": {
      const entropy = s.pinLength * Math.log2(10);
      return +entropy.toFixed(2);
    }
    default: {
      let pool = 0;
      if (s.includeUppercase) pool += 26;
      if (s.includeLowercase) pool += 26;
      if (s.includeNumbers) pool += 10;
      if (s.includeSymbols) pool += 32; // estimate for symbol set
      if (!pool) return 0;
      return +(s.length * Math.log2(pool)).toFixed(2);
    }
  }
};

type StrengthInfo = { label: string; color: string };
export const getStrengthInfo = (entropy: number): StrengthInfo => {
  const buckets: Array<{ max: number; info: StrengthInfo }> = [
    { max: 40, info: { label: "Very Weak", color: "bg-red-500" } },
    { max: 60, info: { label: "Weak", color: "bg-orange-500" } },
    { max: 80, info: { label: "Fair", color: "bg-yellow-500" } },
    { max: 100, info: { label: "Good", color: "bg-blue-500" } },
  ];
  const found = buckets.find(b => entropy < b.max);
  return found ? found.info : { label: "Strong", color: "bg-green-500" };
};

export const estimateCrackTime = (entropy: number): string => {
  if (entropy === 0) return "N/A";
  const guessesPerSecond = 1e12; // 1T guesses/sec
  const seconds = Math.pow(2, entropy) / guessesPerSecond;

  if (seconds < 1) return "Instantly";
  if (seconds < 60) return `${Math.round(seconds)} seconds`;
  if (seconds < 3_600) return `${Math.round(seconds / 60)} minutes`;
  if (seconds < 86_400) return `${Math.round(seconds / 3_600)} hours`;
  if (seconds < YEAR_SECONDS) return `${Math.round(seconds / 86_400)} days`;
  if (seconds < YEAR_SECONDS * 100) return `${Math.round(seconds / YEAR_SECONDS)} years`;
  if (seconds < YEAR_SECONDS * 100_000)
    // keeps original semantics: value in years, label "thousand years"
    return `${Math.round(seconds / YEAR_SECONDS)} thousand years`;
  return "Centuries";
};
